/**
 * PhotoUpload Component
 *
 * A photo upload component that supports camera capture (on mobile),
 * gallery selection, and drag-and-drop. Allows uploading 1-3 photos
 * with preview, progress, and removal functionality.
 */

import {
  useState,
  useRef,
  useCallback,
  useEffect,
  type CSSProperties,
  type ChangeEvent,
  type DragEvent,
} from 'react';
import { usePhotoUpload, ALLOWED_MIME_TYPES, type PhotoUploadResult } from '@/hooks/usePhotoUpload';

/**
 * Props for PhotoUpload component
 */
interface PhotoUploadProps {
  /** Called when photos are successfully uploaded */
  onUploadComplete: (results: PhotoUploadResult[]) => void;
  /** Called when upload starts */
  onUploadStart?: () => void;
  /** Called when upload fails */
  onUploadError?: (error: string) => void;
  /** Route ID for storage path */
  routeId: string;
  /** Initial photos (for editing) */
  initialPhotos?: PhotoPreview[];
  /** Maximum number of photos allowed (default: 3) */
  maxPhotos?: number;
  /** Minimum number of photos required (default: 1) */
  minPhotos?: number;
  /** Whether component is disabled */
  disabled?: boolean;
}

/**
 * Photo preview data
 */
export interface PhotoPreview {
  /** Unique ID for the photo */
  id: string;
  /** Preview URL (local blob or remote URL) */
  previewUrl: string;
  /** Original file (null if from server) */
  file: File | null;
  /** Storage path if already uploaded */
  storagePath?: string;
  /** Download URL if already uploaded */
  downloadUrl?: string;
}

/**
 * Generate unique ID for photo preview
 */
const generatePhotoId = (): string => {
  return `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * PhotoUpload component for uploading route photos
 *
 * Features:
 * - Camera capture on mobile (environment-facing camera)
 * - Gallery selection for multiple photos
 * - Preview before upload
 * - Upload progress indicator
 * - Remove photo functionality
 * - 10MB max file size validation
 * - JPEG, PNG, WebP support
 *
 * @example
 * ```tsx
 * <PhotoUpload
 *   routeId={routeId}
 *   onUploadComplete={(results) => setPhotoUrls(results.map(r => r.url))}
 *   onUploadError={(error) => setError(error)}
 * />
 * ```
 */
export const PhotoUpload = ({
  onUploadComplete,
  onUploadStart,
  onUploadError,
  routeId,
  initialPhotos = [],
  maxPhotos = 3,
  minPhotos = 1,
  disabled = false,
}: PhotoUploadProps) => {
  // Photo previews (local files and existing photos)
  const [photos, setPhotos] = useState<PhotoPreview[]>(initialPhotos);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // File input refs
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Track photos for cleanup on unmount
  const photosRef = useRef<PhotoPreview[]>(photos);

  // Track if we've done initial sync - prevents re-syncing during normal operation
  const hasInitializedRef = useRef(false);

  // Upload hook
  const {
    uploadPhotos,
    uploading,
    progressList,
    error: uploadError,
    validateFiles,
  } = usePhotoUpload();

  /**
   * Keep photosRef in sync with photos state for cleanup
   */
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  /**
   * Restore photos from initialPhotos on mount if we have them
   * This handles the tab switch case where component remounts with empty state
   * Only runs once on mount to prevent duplicate syncing during normal operation
   */
  useEffect(() => {
    // Only sync on initial mount, not on subsequent prop changes
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    // If we have initialPhotos (from parent state), restore them
    if (initialPhotos.length > 0 && photos.length === 0) {
      setPhotos(initialPhotos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  /**
   * Cleanup blob URLs on component unmount to prevent memory leaks
   */
  useEffect(() => {
    return () => {
      photosRef.current.forEach((photo) => {
        if (photo.file && photo.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(photo.previewUrl);
        }
      });
    };
  }, []); // Only run cleanup on unmount

  /**
   * Upload files immediately after adding them
   */
  const uploadFilesImmediately = useCallback(
    async (newPreviews: PhotoPreview[], allPhotos: PhotoPreview[]) => {
      const filesToUpload = newPreviews.filter((p) => p.file !== null).map((p) => p.file as File);

      if (filesToUpload.length === 0) return;

      onUploadStart?.();

      try {
        const results = await uploadPhotos(filesToUpload, routeId);

        // Update photos with upload results
        const updatedPhotos = allPhotos.map((photo) => {
          if (photo.file) {
            const result = results.find((r) => r.file === photo.file);
            if (result) {
              return {
                ...photo,
                downloadUrl: result.url,
                storagePath: result.storagePath,
              };
            }
          }
          return photo;
        });

        setPhotos(updatedPhotos);

        // Create final results including existing photos
        const allResults: PhotoUploadResult[] = updatedPhotos.map((p) => ({
          file: p.file ?? new File([], 'existing'),
          url: p.downloadUrl ?? p.previewUrl,
          storagePath: p.storagePath ?? '',
        }));

        onUploadComplete(allResults);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        onUploadError?.(errorMessage);
      }
    },
    [routeId, uploadPhotos, onUploadStart, onUploadComplete, onUploadError]
  );

  /**
   * Handle file selection
   */
  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setValidationError(null);

    // Convert FileList to array
    const fileArray = Array.from(files);

    // Check if adding these would exceed max
    const totalPhotos = photos.length + fileArray.length;
    if (totalPhotos > maxPhotos) {
      setValidationError(
        `Maximum ${maxPhotos} photos allowed. You can add ${maxPhotos - photos.length} more.`
      );
      event.target.value = '';
      return;
    }

    // Validate files
    const validation = validateFiles(fileArray);
    if (!validation.valid) {
      setValidationError(validation.errors.join('. '));
      event.target.value = '';
      return;
    }

    // Create previews
    const newPreviews: PhotoPreview[] = fileArray.map((file) => ({
      id: generatePhotoId(),
      previewUrl: URL.createObjectURL(file),
      file,
    }));

    const allPhotos = [...photos, ...newPreviews];
    setPhotos(allPhotos);

    // Auto-upload immediately
    uploadFilesImmediately(newPreviews, allPhotos);

    // Reset input
    event.target.value = '';
  };

  /**
   * Process files from any source (file input or drag-and-drop)
   */
  const processFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);

      if (fileArray.length === 0) return;

      setValidationError(null);

      // Check if adding these would exceed max
      const totalPhotos = photos.length + fileArray.length;
      if (totalPhotos > maxPhotos) {
        setValidationError(
          `Maximum ${maxPhotos} photos allowed. You can add ${maxPhotos - photos.length} more.`
        );
        return;
      }

      // Validate files
      const validation = validateFiles(fileArray);
      if (!validation.valid) {
        setValidationError(validation.errors.join('. '));
        return;
      }

      // Create previews
      const newPreviews: PhotoPreview[] = fileArray.map((file) => ({
        id: generatePhotoId(),
        previewUrl: URL.createObjectURL(file),
        file,
      }));

      const allPhotos = [...photos, ...newPreviews];
      setPhotos(allPhotos);

      // Auto-upload immediately
      uploadFilesImmediately(newPreviews, allPhotos);
    },
    [photos, maxPhotos, validateFiles, uploadFilesImmediately]
  );

  /**
   * Handle drag over event
   */
  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!disabled && !uploading && photos.length < maxPhotos) {
        setIsDragOver(true);
      }
    },
    [disabled, uploading, photos.length, maxPhotos]
  );

  /**
   * Handle drag leave event
   */
  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  }, []);

  /**
   * Handle drop event
   */
  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);

      if (disabled || uploading) return;

      const files = event.dataTransfer.files;
      if (files.length > 0) {
        processFiles(files);
      }
    },
    [disabled, uploading, processFiles]
  );

  /**
   * Remove a photo from the list
   */
  const handleRemovePhoto = (id: string) => {
    setPhotos((prev) => {
      const photo = prev.find((p) => p.id === id);

      // Revoke blob URL if it's a local file
      if (photo?.file && photo.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(photo.previewUrl);
      }

      return prev.filter((p) => p.id !== id);
    });
  };

  /**
   * Open camera input (mobile)
   */
  const handleCameraClick = () => {
    if (photos.length >= maxPhotos) {
      setValidationError(`Maximum ${maxPhotos} photos allowed`);
      return;
    }
    cameraInputRef.current?.click();
  };

  /**
   * Open gallery input
   */
  const handleGalleryClick = () => {
    if (photos.length >= maxPhotos) {
      setValidationError(`Maximum ${maxPhotos} photos allowed`);
      return;
    }
    galleryInputRef.current?.click();
  };

  // Calculate overall progress
  const overallProgress =
    progressList.length > 0
      ? Math.round(progressList.reduce((sum, p) => sum + p.progress, 0) / progressList.length)
      : 0;

  const canAddMore = photos.length < maxPhotos;
  const displayError = validationError ?? uploadError;

  return (
    <div
      style={containerStyle}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        style={hiddenInputStyle}
        disabled={disabled || uploading}
        aria-label="Take photo with camera"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept={ALLOWED_MIME_TYPES.join(',')}
        multiple
        onChange={handleFileSelect}
        style={hiddenInputStyle}
        disabled={disabled || uploading}
        aria-label="Select photos from gallery"
      />

      {/* Drop zone overlay when dragging */}
      {isDragOver && (
        <div style={dropZoneOverlayStyle}>
          <div style={dropZoneContentStyle}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            <span>Drop photos here</span>
          </div>
        </div>
      )}

      {/* Photo previews grid */}
      {photos.length > 0 && (
        <div style={previewGridStyle}>
          {photos.map((photo, index) => (
            <div key={photo.id} style={previewItemStyle}>
              <img src={photo.previewUrl} alt={`Photo ${index + 1}`} style={previewImageStyle} />

              {/* Remove button */}
              {!uploading && !disabled && (
                <button
                  type="button"
                  onClick={() => handleRemovePhoto(photo.id)}
                  style={removeButtonStyle}
                  aria-label={`Remove photo ${index + 1}`}
                >
                  X
                </button>
              )}

              {/* Upload progress overlay */}
              {uploading && progressList[index] && !progressList[index].completed && (
                <div style={progressOverlayStyle}>
                  <div style={progressBarContainerStyle}>
                    <div
                      style={{
                        ...progressBarStyle,
                        width: `${progressList[index].progress}%`,
                      }}
                    />
                  </div>
                  <span style={progressTextStyle}>{progressList[index].progress}%</span>
                </div>
              )}

              {/* Photo order badge */}
              <div style={orderBadgeStyle}>{index + 1}</div>
            </div>
          ))}
        </div>
      )}

      {/* Add photo buttons */}
      {canAddMore && !uploading && (
        <div style={buttonRowStyle}>
          <button
            type="button"
            onClick={handleCameraClick}
            disabled={disabled}
            style={{
              ...addButtonStyle,
              ...(disabled ? disabledButtonStyle : {}),
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ marginBottom: '4px' }}
            >
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            <span>Take Photo</span>
          </button>
          <button
            type="button"
            onClick={handleGalleryClick}
            disabled={disabled}
            style={{
              ...addButtonStyle,
              ...(disabled ? disabledButtonStyle : {}),
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ marginBottom: '4px' }}
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            <span>Choose Files</span>
          </button>
        </div>
      )}

      {/* Drag and drop hint */}
      {canAddMore && !uploading && <p style={dragHintStyle}>or drag and drop photos here</p>}

      {/* Photo count and requirements */}
      <div style={requirementsStyle}>
        <span style={photos.length >= minPhotos ? requirementsMetStyle : requirementsUnmetStyle}>
          {photos.length} / {maxPhotos} photos
        </span>
        {photos.length < minPhotos && (
          <span style={requirementsUnmetStyle}> (min {minPhotos} required)</span>
        )}
      </div>

      {/* Error message */}
      {displayError && <div style={errorStyle}>{displayError}</div>}

      {/* Upload progress indicator */}
      {uploading && (
        <div style={uploadingIndicatorStyle}>
          <span>Uploading... {overallProgress}%</span>
        </div>
      )}

      {/* File size info */}
      <p style={helpTextStyle}>JPEG, PNG, or WebP. Maximum 10MB per photo.</p>
    </div>
  );
};

/**
 * Component styles - using CSS variables for dark mode support
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  position: 'relative',
};

const hiddenInputStyle: CSSProperties = {
  display: 'none',
};

const dropZoneOverlayStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'var(--color-badge-info-bg)',
  border: '3px dashed var(--color-primary)',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
};

const dropZoneContentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  color: 'var(--color-primary)',
  fontSize: '1.125rem',
  fontWeight: 600,
};

const previewGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '12px',
};

const previewItemStyle: CSSProperties = {
  position: 'relative',
  aspectRatio: '1',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid var(--color-border)',
};

const previewImageStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const removeButtonStyle: CSSProperties = {
  position: 'absolute',
  top: '4px',
  right: '4px',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  border: 'none',
  fontSize: '12px',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const progressOverlayStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
};

const progressBarContainerStyle: CSSProperties = {
  width: '80%',
  height: '4px',
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  borderRadius: '2px',
  overflow: 'hidden',
};

const progressBarStyle: CSSProperties = {
  height: '100%',
  backgroundColor: 'var(--color-primary)',
  transition: 'width 0.2s',
};

const progressTextStyle: CSSProperties = {
  color: 'white',
  fontSize: '12px',
  fontWeight: 500,
};

const orderBadgeStyle: CSSProperties = {
  position: 'absolute',
  bottom: '4px',
  left: '4px',
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  fontSize: '11px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const buttonRowStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
};

const addButtonStyle: CSSProperties = {
  flex: 1,
  padding: '16px',
  borderRadius: '8px',
  border: '2px dashed var(--color-border)',
  backgroundColor: 'var(--color-bg-tertiary)',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  transition: 'border-color 0.2s, background-color 0.2s',
};

const disabledButtonStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const dragHintStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
  textAlign: 'center',
  margin: 0,
};

const requirementsStyle: CSSProperties = {
  fontSize: '0.875rem',
  textAlign: 'center',
};

const requirementsMetStyle: CSSProperties = {
  color: 'var(--color-success)',
  fontWeight: 500,
};

const requirementsUnmetStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
};

const errorStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  border: '1px solid var(--color-error)',
  color: 'var(--color-badge-warning-text)',
  fontSize: '0.875rem',
  textAlign: 'center',
};

const uploadingIndicatorStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-info-bg)',
  color: 'var(--color-primary)',
  fontSize: '0.875rem',
  fontWeight: 500,
  textAlign: 'center',
};

const helpTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
  textAlign: 'center',
  margin: 0,
};

export default PhotoUpload;
